import {useState} from 'react'
import useSWRImmutable from 'swr/immutable'
import {
  Box,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Icon,
  Text,
  Divider,
  useClipboard,
} from '@chakra-ui/react'
import {Trans, useTranslation} from 'react-i18next'

import {CopyIcon, CheckIcon, DownloadIcon} from '@chakra-ui/icons'
import {CogWithSparkleIcon} from '../../icons'
import {fetcher} from '../../../api'
import {useRails} from '../../contexts/rails'
import ButtonWithIcon from '../ButtonWithIcon'
import CopyField from '../CopyField'
import ConfettiModalOverlay from '../../animations/ConfettiModalOverlay'

const TIMEOUT = 2000

const BankingDetailsPopup = () => {
  const {
    organisation: {currentSupplier},
  } = useRails()
  const {data, isLoading} = useSWRImmutable(
    `/api/suppliers/${currentSupplier?.id}/banking_details_transitions/banking_details_popup`,
    fetcher,
  )

  const {t} = useTranslation('bankingDetailsModal')

  const [isDismissed, setIsDismissed] = useState(false)
  const [isDownloaded, setIsDownloaded] = useState(false)
  const onDownload = () => {
    setIsDownloaded(true)
    setTimeout(() => setIsDownloaded(false), TIMEOUT)
  }

  const bankingDetailsCopyText = data
    ? `Account Name: ${data.account_name}\nBSB: ${data.bsb}\nAccount: ${data.account_number}`
    : ''

  const {onCopy, hasCopied} = useClipboard(bankingDetailsCopyText, {
    timeout: TIMEOUT,
  })

  const onClose = () => {
    setIsDismissed(true)
  }

  const displayBankingDetails =
    !isDismissed &&
    !data?.banking_details_displayed_at &&
    data?.banking_details_transition_exists

  if (displayBankingDetails && !isLoading) {
    return (
      <Modal isOpen onClose={onClose}>
        <ModalOverlay>
          <ConfettiModalOverlay />
        </ModalOverlay>
        <ModalContent maxW="550px">
          {/* Title */}
          <Flex justifyContent="space-between" py="sm" px="md">
            <Text pt={1} lineHeight="1" letterSpacing="-4%" fontSize="xl">
              {t('title')}
            </Text>
            <ModalCloseButton position="center" align="center" />
          </Flex>

          <Divider borderColor="mld.neutral.1000" width="92%" mx="md" />

          {/* Account details */}
          <ModalBody>
            <Box>
              <Text
                fontSize="14px"
                lineHeight="110%"
                letterSpacing="-1%"
                py="xs"
                textTransform="uppercase"
              >
                {t('subtitle')}
              </Text>

              <Text
                fontSize="sm"
                lineHeight="110%"
                letterSpacing="-1%"
                pt="xs"
                pb="sm"
                maxWidth="95%"
              >
                {t('headline')}
              </Text>
              <CopyField title={t('account.name')} value={data.account_name} />
              <Flex py="sm" gap={4}>
                <CopyField title={t('account.bsb')} value={data.bsb} />
                <CopyField
                  title={t('account.number')}
                  value={data.account_number}
                />
              </Flex>
            </Box>

            <Divider borderColor="mld.neutral.500" my={2} />

            {/* Footer */}
            <Box py={4}>
              <Flex justifyContent="space-between" pb="sm" gap={3}>
                <Icon as={CogWithSparkleIcon} boxSize="5" />
                <Text>
                  <Trans
                    t={t}
                    i18nKey="footer"
                    components={{
                      bold: <b />,
                    }}
                  />
                </Text>
              </Flex>
              <Flex gap={4}>
                <ButtonWithIcon
                  variant="primary"
                  text={t('button.copy')}
                  icon={hasCopied ? <CheckIcon /> : <CopyIcon />}
                  onClick={onCopy}
                />
                <ButtonWithIcon
                  variant="secondary"
                  text={t('button.download')}
                  icon={<Icon as={DownloadIcon} />}
                  href={`/api/suppliers/${currentSupplier?.id}/banking_details_transitions/download_supplier_bank_statement`}
                  disabled={isDownloaded}
                  onClick={onDownload}
                />
              </Flex>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    )
  }

  return null
}

export default BankingDetailsPopup
